import React, { useEffect, useState } from "react";
import styles from "../../styles/environment/dashboard.module.css";
import LineChart from "../../Components/GraphNCharts/LineChart";
import PieChart from "../../Components/GraphNCharts/Pie";
import EchartDoughnutChart from "../../Components/GraphNCharts/EchartDoughnut";
import { monthSort } from "../../utils/constant";
import Cards from "../../Components/Environment/Cards";
import { Table, TableBody, TableCell, TableRow } from "@mui/material";
import { get } from "../../utils/api";
import GlobalChart from "../../Components/GraphNCharts/GlobalChart";
import PeriodFilter from "../../Components/Common/PeriodFilter";
import {
  fixedTons,
  formatNumberCommaSeparated,
  truncateNumber,
} from "../../utils/common";
import useAuthStore from "../../store/ContextUser";
import { useUser } from "../../context/AuthContext";
import BarChart from "../../Components/GraphNCharts/BarChart";
import { ContactsOutlined, TrendingUp } from "@mui/icons-material";

const DataCard = ({ title, titleValue, data }) => {
  return (
    <div className={styles.data_card}>
      <p className={styles.data_card_title}>
        {title}
        <span className={styles.graph_span}>{titleValue}</span>
      </p>

      <Table>
        <TableBody>
          {data.map((item, index) => (
            <TableRow key={index}>
              <TableCell
                sx={{ borderBottom: "none", padding: "0", width: "70%" }}
              >
                <p className={styles.table_row}>{item.label}:</p>
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  borderBottom: "none",
                  padding: "0",
                }}
              >
                <p className={styles.table_value}>
                  {formatNumberCommaSeparated(item.value)}
                </p>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const Dashboard = ({ showCompany = false }) => {
  const { user } = useUser();
  const { selectedCompany } = useAuthStore();
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const [selectedFacility, setSelectedFacility] = useState([]);

  const [fromDate, setFromDate] = useState({
    month: 1,
    year: currentYear,
  });
  const [toDate, setToDate] = useState({
    month: currentDate.getMonth() + 1,
    year: currentYear,
  });
  const [emissionType, setEmissionType] = useState("Annual");

  const [dashboardData, setDashboardData] = useState({
    scope1: null,
    scope2: null,
    scope3: null,
  });

  const [totalData, setTotalData] = useState({
    scope1: null,
    scope2: null,
    scope3: null,
  });

  const [waterData, setWaterData] = useState({
    currentData: null,
    percentageChange: null,
  });

  const [waterWithdrawal, setWaterWithdrawal] = useState({});
  const [waterDischarge, setWaterDischarge] = useState({
    source: {
      name: [],
      value: [],
    },
    monthlyData: [],
    annualData: [],
  });
  const [totalChartData, setTotalChartData] = useState(null);
  const [wastewaterTreatedData, setWastewaterTreatedData] = useState(null);
  const [waterRecycledData, setWaterRecycledData] = useState(null);

  const [overviewData, setOverviewData] = useState({
    scope1: null,
    scope2: null,
    scope3: null,
  });

  const [quantityProduced, setQuantityProduced] = useState(1);
  const [activeView, setActiveView] = useState("emissions");
  const [energyIntensityType, setEnergyIntensityType] = useState("machine");
  const [fuelUse, setFuelUse] = useState("Mass Based");
  const [fuelUseByProcess, setFuelUseByProcess] = useState("Mass Based");
  const [fuelData, setFuelData] = useState([
    {
      unit: "",
      Petrol: "",
      Diesel: "",
      "Fuel Oil": "",
      Coal: "",
      "Natural Gas": "",
      LPG: "",
      Propane: "",
    },
  ]);
  const [EnergyData, setEnergyData] = useState([
    {
      unit: "",
      processName: [],
      machineName: [],
    },
  ]);
  const [FuelProcessData, setFuelProcessData] = useState([
    {
      unit: "",
      processName: [],
    },
  ]);
  const [FacilityEnergyData, setFacilityEnergyData] = useState([
    {
      unit: "",
      facilities: [],
      electricity: [],
      steam: [],
      heat: [],
    },
  ]);
  const [TopFuelConsumptionData, setTopFuelConsumptionData] = useState([
    {
      title: "Top Fuel Consuming Processes",
      items: [],
    },
  ]);
  const [TopMachinesData, setTopMachinesData] = useState([
    {
      title: "Top Machines by Usage",
      items: [],
    },
    {
      title: "Top Machines by Emission per Unit",
      items: [],
    },
  ]);
  const [TrendFuelData, setTrendFuelData] = useState({
    labels: [],
    datasets: [],
    units: {},
  });
  const [ProcessEnergyData, setProcessEnergyData] = useState([]);
  const [temporalType, setTemporalType] = useState("monthly");
  const [temporalPeriod, setTemporalPeriod] = useState("January 2024");

  // Define temporal period options based on type
  const getTemporalPeriodOptions = (type) => {
    if (type === "quarter") {
      return ["Jan-Mar 2024", "Apr-Jun 2024", "Jul-Sep 2024", "Oct-Dec 2024"];
    } else {
      return [
        "January 2024",
        "February 2024",
        "March 2024",
        "April 2024",
        "May 2024",
        "June 2024",
        "July 2024",
        "August 2024",
        "September 2024",
        "October 2024",
        "November 2024",
        "December 2024",
      ];
    }
  };
  // Hotspot Analysis Data
  const hotspotAnalysisData = [
    {
      title: "Top Fuel Consuming Processes",
      items: [
        { name: "Smelting", value: "12,500 litres/month" },
        { name: "Heating", value: "9,800 litres/month" },
        { name: "Drying", value: "7,400 litres/month" },
      ],
    },
    {
      title: "Top Machines by Fuel/Energy Use",
      items: [
        { name: "Furnace", value: "15,200" },
        { name: "Boiler", value: "12,700" },
        { name: "Compressor Unit", value: "10,500" },
      ],
    },
    {
      title: "Machines with Highest Emission per Unit",
      items: [
        { name: "Old Generator Set", value: "0.85kg CO2/kWh" },
        { name: "Kiln", value: "0.85kg CO2/kWh" },
        { name: "Welding Machine", value: "0.85kg CO2/kWh" },
      ],
    },
  ];

  // Temporal Trends Data
  // Dynamic temporal trends labels based on temporal type
  const getTemporalTrendsLabels = (type) => {
    if (type === "quarter") {
      return ["Jan-Mar", "Apr-Jun", "Jul-Sep", "Oct-Dec"];
    } else {
      return [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
    }
  };

  const temporalTrendsLabels = getTemporalTrendsLabels(temporalType);

  // Use dynamic TrendFuelData instead of static temporalTrendsData
  const temporalTrendsData =
    TrendFuelData.datasets.length > 0
      ? TrendFuelData.datasets
      : [
          {
            label: "Diesel",
            data: [0, 0, 0, 0],
            borderColor: "#FF4F4F",
            backgroundColor: "rgba(255, 79, 79, 0.1)",
            tension: 0.4,
          },
        ];

  const apiurls = {
    scope1: `/dashboard/enviornment/scope1?startYear=${
      fromDate.year
    }&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${
      toDate.month
    }&facilities=${selectedFacility.join(",")}&companyId=${
      selectedCompany?._id
    }`,
    scope2: `/dashboard/enviornment/scope2?startYear=${
      fromDate.year
    }&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${
      toDate.month
    }&facilities=${selectedFacility.join(",")}&companyId=${
      selectedCompany?._id
    }`,
    scope3: `/dashboard/enviornment/scope3?startYear=${
      fromDate.year
    }&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${
      toDate.month
    }&facilities=${selectedFacility.join(",")}&companyId=${
      selectedCompany?._id
    }`,
  };

  const totalDataApiurls = {
    scope1: `/dashboard/enviornment/ghg/scope1/totalEmission?startYear=${
      fromDate.year
    }&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${
      toDate.month
    }&facilities=${selectedFacility.join(",")}&companyId=${
      selectedCompany?._id
    }`,
    scope2: `/dashboard/enviornment/ghg/scope2/totalEmission?startYear=${
      fromDate.year
    }&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${
      toDate.month
    }&facilities=${selectedFacility.join(",")}&companyId=${
      selectedCompany?._id
    }`,
    scope3: `/dashboard/enviornment/ghg/scope3/totalEmission?startYear=${
      fromDate.year
    }&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${
      toDate.month
    }&facilities=${selectedFacility.join(",")}&companyId=${
      selectedCompany?._id
    }`,
  };

  const waterDataApiurls = {
    waterapi: `dashboard/enviornment/ghg/waterFinalDataForGhg?startYear=${
      fromDate.year
    }&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${
      toDate.month
    }&facilities=${selectedFacility.join(",")}&companyId=${
      selectedCompany?._id
    }`,
    totalChart: `/scope3/waterManagement/dashboard/total?startYear=${
      fromDate.year
    }&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${
      toDate.month
    }&facilities=${selectedFacility.join(",")}&companyId=${
      selectedCompany?._id
    }`,
  };

  const overviewApiUrls = {
    scope1: `/dashboard/enviornment/overview/dateFilter/scope1?startYear=${
      fromDate.year
    }&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${
      toDate.month
    }&facilities=${selectedFacility.join(",")}&companyId=${
      selectedCompany?._id
    }`,
    scope2: `/dashboard/enviornment/overview/dateFilter/scope2?startYear=${
      fromDate.year
    }&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${
      toDate.month
    }&facilities=${selectedFacility.join(",")}&companyId=${
      selectedCompany?._id
    }`,
    scope3: `/dashboard/enviornment/overview/dateFilter/scope3?startYear=${
      fromDate.year
    }&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${
      toDate.month
    }&facilities=${selectedFacility.join(",")}&companyId=${
      selectedCompany?._id
    }`,
  };

  const generateYearList = (startYear = 2020) => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = startYear; year <= currentYear; year++) {
      years.push(year);
    }
    return years;
  };

  const handleViewSwitch = async (view) => {
    setActiveView(view);

    // Make API call based on the selected view
    try {
      if (view === "emissions") {
        // Fetch GHG and Water data
        await fetchData();
        await fetchTotalData();
        await fetchOverviewData();
        await fetchWaterData();
      } else if (view === "energy") {
        // Fetch Energy and Fuel data
        // TODO: Add energy and fuel API calls when available
        console.log("Energy and Fuel view selected");
      }
    } catch (error) {
      console.error("Error switching views:", error);
    }
  };
  const yearList = generateYearList(2020);

  const fetchData = async () => {
    try {
      const [scope1, scope2, scope3] = await Promise.all([
        get(apiurls.scope1),
        get(apiurls.scope2),
        get(apiurls.scope3),
      ]);
      setDashboardData({
        scope1: scope1.data,
        scope2: scope2.data,
        scope3: scope3.data,
      });
    } catch (error) {
      setDashboardData({
        scope1: null,
        scope2: null,
        scope3: null,
      });
    }
  };

  useEffect(() => {
    fetchData();
  }, [fromDate, toDate, selectedFacility, selectedCompany]);

  const fetchProductionData = async (req, res) => {
    try {
      const [data] = await Promise.all([
        get(
          `/dashboard/enviornment/ghg/production?startYear=${
            fromDate.year
          }&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${
            toDate.month
          }&facilities=${selectedFacility.join(",")}&companyId=${
            selectedCompany?._id
          }`,
        ),
      ]);
      setQuantityProduced(data?.data?.quantityProduced);
    } catch (error) {
      setWaterData({
        currentData: null,
      });
    }
  };

  useEffect(() => {
    fetchProductionData();
  }, [fromDate, toDate, selectedFacility, selectedCompany]);

  const fetchTotalData = async () => {
    try {
      const [scope1, scope2, scope3] = await Promise.all([
        get(totalDataApiurls.scope1),
        get(totalDataApiurls.scope2),
        get(totalDataApiurls.scope3),
      ]);
      setTotalData({
        scope1: scope1.data,
        scope2: scope2.data,
        scope3: scope3.data,
      });
    } catch (error) {
      setTotalData({
        scope1: null,
        scope2: null,
        scope3: null,
      });
    }
  };

  useEffect(() => {
    fetchTotalData();
  }, [fromDate, toDate, selectedFacility, selectedCompany]);

  const fetchWaterData = async () => {
    try {
      const [
        waterData,
        withdrawal,
        discharge,
        totalChart,
        wastewaterTreated,
        waterRecycled,
      ] = await Promise.all([
        get(waterDataApiurls.waterapi),
        get(waterDataApiurls.withdrawal),
        get(waterDataApiurls.discharge),
        get(waterDataApiurls.totalChart),
        get(waterDataApiurls.wastewaterTreated),
        get(waterDataApiurls.waterRecycled),
      ]);

      setWaterData({
        currentData: waterData?.data?.currentYearResult[0],
        percentageChange: waterData?.data?.percentageChange,
      });

      setWaterWithdrawal(withdrawal?.data);
      setWaterDischarge(discharge?.data);
      setTotalChartData(totalChart?.data);
      setWastewaterTreatedData(wastewaterTreated?.data);
      setWaterRecycledData(waterRecycled?.data);
    } catch (error) {
      setWaterData({
        currentData: null,
        percentageChange: null,
      });
      setWaterWithdrawal({});
      setWaterDischarge({
        source: { name: [], value: [] },
        monthlyData: [],
        annualData: [],
      });
      setTotalChartData(null);
      setWastewaterTreatedData(null);
      setWaterRecycledData(null);
    }
  };

  useEffect(() => {
    fetchWaterData();
  }, [fromDate, toDate, selectedFacility, selectedCompany]);

  const fetchOverviewData = async () => {
    try {
      const [scope1, scope2, scope3] = await Promise.all([
        get(overviewApiUrls.scope1),
        get(overviewApiUrls.scope2),
        get(overviewApiUrls.scope3),
      ]);
      setOverviewData({
        scope1: scope1?.data?.environmentDashboardRes,
        scope2: scope2?.data?.environmentDashboardRes,
        scope3: scope3?.data?.environmentDashboardRes,
      });
    } catch (error) {
      setOverviewData({
        scope1: null,
        scope2: null,
        scope3: null,
      });
    }
  };

  useEffect(() => {
    fetchOverviewData();
  }, [fromDate, toDate, selectedFacility, selectedCompany]);

  // Handle fuel use type and energy intensity type changes

  // Call API when component mounts or fuelUse changes
  useEffect(() => {
    if (activeView === "energy" && fuelUse) {
      TotalFuelUsedApi(fuelUse);
    }
  }, [fuelUse, activeView, fromDate, toDate]);

  // Call Energy Consumption API when component mounts or energyIntensityType changes
  useEffect(() => {
    if (activeView === "energy" && energyIntensityType) {
      EnergyConsumptionApi(energyIntensityType);
    }
  }, [energyIntensityType, activeView, fromDate, toDate]);

  // Call Facility Energy Intensity API when component mounts or activeView changes
  useEffect(() => {
    if (activeView === "energy") {
      FacilityEnergyIntensityApi();
    }
  }, [activeView, fromDate, toDate]);

  // Call Top Fuel Consumption Process API when component mounts, activeView changes, or date range changes
  useEffect(() => {
    console.log(fromDate, toDate, yearList, "checking for the data");
    if (activeView === "energy") {
      TopFuelConsumptionProcessApi();
    }
  }, [activeView, fromDate, toDate]);

  // Call Top Machines API when component mounts, activeView changes, or date range changes
  useEffect(() => {
    if (activeView === "energy") {
      TopMachinesApi();
    }
  }, [activeView, fromDate, toDate]);

  // Call Trend Fuel Type API when component mounts, activeView changes, emission type changes, or date range changes
  useEffect(() => {
    if (activeView === "energy" && temporalType) {
      TrendFuelTypeApi(temporalType);
    }
  }, [activeView, fromDate, toDate, temporalType, temporalPeriod]);

  useEffect(() => {
    if (activeView === "energy" && fuelUseByProcess) {
      FuelUsedByProcessApi(fuelUseByProcess);
    }
  }, [fuelUseByProcess, activeView, fromDate, toDate]);

  useEffect(() => {
    if (activeView === "energy") {
      ProcessWiseEnergyConsumptionApi();
    }
  }, [activeView, fromDate, toDate]);

  // Helper function to get current fuel data based on selection

  let dataset =
    overviewData?.scope1?.scopoe1MonthlEmission?.data
      ?.map((item) => item?.totalEmission)
      ?.filter(Boolean)?.length > 0 ||
    overviewData?.scope3?.scope3Emission
      ?.map((item) => item?.totalEmission)
      ?.filter(Boolean)?.length > 0 ||
    overviewData?.scope2?.scope2MontlyEmission
      ?.map((item) => item?.totalEmission)
      ?.filter(Boolean)?.length > 0
      ? [
          {
            label: "Scope 1",
            data: overviewData?.scope1?.scopoe1MonthlEmission?.data?.map(
              (item) => item?.totalEmission,
            ) ?? [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
            borderColor: "rgba(255, 68, 68, 1)",
            backgroundColor: "rgba(255, 68, 68, 1)",
            tension: 0.4,
            fill: true,
            pointRadius: 0,
          },
          {
            label: "Scope 2",
            data: overviewData?.scope2?.scope2MontlyEmission?.map(
              (item) => item?.totalEmission,
            ) ?? [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
            borderColor: "rgba(255, 187, 51, 1)",
            backgroundColor: "rgba(255, 187, 51, 1)",
            tension: 0.4,
            fill: true,
            pointRadius: 0,
          },
          {
            label: "Scope 3",
            data: overviewData?.scope3?.scope3Emission?.map(
              (item) => item?.totalEmission,
            ) ?? [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
            borderColor: "rgba(0, 102, 231, 1)",
            backgroundColor: "rgba(0, 102, 231, 1)",
            tension: 0.4,
            fill: true,
            pointRadius: 0,
          },
        ]
      : [];

  let yoydataset =
    overviewData?.scope1?.scope1Last10YearData
      ?.map((item) => item?.totalEmission)
      ?.filter(Boolean)?.length > 0 ||
    overviewData?.scope2?.last10yearScope2FinalEmission
      ?.map((item) => item?.finalEmission)
      ?.filter(Boolean)?.length > 0 ||
    overviewData?.scope3?.scope3Last10YearData
      ?.map((item) => item?.totalEmission)
      ?.filter(Boolean)?.length > 0
      ? [
          {
            label: "Scope 1",
            data: overviewData?.scope1?.scope1Last10YearData?.map(
              (item) => item?.totalEmission,
            ) ?? [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
            borderColor: "rgba(255, 68, 68, 1)",
            backgroundColor: "rgba(255, 68, 68, 0.6)",
            tension: 0.4,
            fill: true,
            pointBorderColor: "#fff",
            pointRadius: 0,
          },
          {
            label: "Scope 2",
            data: overviewData?.scope2?.last10yearScope2FinalEmission?.map(
              (item) => item?.finalEmission,
            ) ?? [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
            borderColor: "rgba(255, 187, 51, 1)",
            backgroundColor: "rgba(255, 187, 51, 0.6)",
            tension: 0.3,
            fill: true,
            pointRadius: 0,
          },
          {
            label: "Scope 3",
            data: overviewData?.scope3?.scope3Last10YearData?.map(
              (item) => item?.totalEmission,
            ) ?? [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
            borderColor: "rgba(0, 102, 231, 1)",
            backgroundColor: "rgba(0, 102, 231, 0.6)",
            tension: 0.3,
            fill: true,
            pointRadius: 0,
          },
        ]
      : [];

  const scope1Data = [
    {
      label: "Stationary Combustion",
      value: `${fixedTons(totalData?.scope1?.stationNaryCombusionSum) || 0}`,
    },
    {
      label: "Mobile Combustion",
      value: `${fixedTons(totalData?.scope1?.mobileCombusionSum) || 0}`,
    },
    {
      label: "Fugitive Emissions",
      value: `${fixedTons(totalData?.scope1?.fugitiveCombusionSum) || 0}`,
    },
  ];

  const scope2Data = [
    {
      label: "Electricity",
      value: `${fixedTons(totalData?.scope2?.electricity) || 0}`,
    },
    {
      label: "Heating and Cooling",
      value: `${fixedTons(totalData?.scope2?.heat) || 0}`,
    },
    {
      label: "Steam",
      value: `${fixedTons(totalData?.scope2?.steam) || 0}`,
    },
  ];

  const scope3Data = [
    {
      label: "Purchased Goods and Services",
      value: `${fixedTons(totalData?.scope3?.purchaseGoodServicesSum) || 0}`,
    },
    {
      label: "Upstream Transportation and Distribution",
      value: `${
        fixedTons(totalData?.scope3?.sumOfUpstreamAndDownstream?.Upstream) || 0
      }`,
    },
    {
      label: "Downstream Transportation and Distribution",
      value: `${
        fixedTons(totalData?.scope3?.sumOfUpstreamAndDownstream?.Downstream) ||
        0
      }`,
    },
    {
      label: "Employee Commute",
      value: `${fixedTons(totalData?.scope3?.employeCommuteSum) || 0}`,
    },
    {
      label: "Business Travel",
      value: `${fixedTons(totalData?.scope3?.businessSum) || 0}`,
    },
    {
      label: "Waste Generated in Operation",
      value: `${fixedTons(totalData?.scope3?.wasteGeneratedSum) || 0}`,
    },
  ];

  const withdrawalChartData = totalChartData?.data?.find(
    (item) => item?.type === "Water Withdrawal",
  );
  const dischargeChartData = totalChartData?.data?.find(
    (item) => item?.type === "Water Discharge",
  );

  const wastewaterTreatedChartData = totalChartData?.data?.find(
    (item) => item?.type === "Water Treated",
  );
  const waterRecycledChartData = totalChartData?.data?.find(
    (item) => item?.type === "Water Recycled",
  );

  // Filter data at source to remove empty labels
  const filteredWithdrawalData =
    withdrawalChartData?.data?.filter((item) => item?.label !== "") || [];
  const filteredDischargeData =
    dischargeChartData?.data?.filter((item) => item?.label !== "") || [];
  const filteredWastewaterTreatedData =
    wastewaterTreatedChartData?.data
      ?.filter((item) => item?.label !== "")
      ?.sort((a, b) => a?.label?.localeCompare(b?.label)) || [];
  const filteredWaterRecycledData =
    waterRecycledChartData?.data?.filter((item) => item?.label !== "") || [];

  console.log(
    wastewaterTreatedChartData?.data?.filter((item) => item?.label !== ""),
    waterRecycledChartData?.data?.filter((item) => item?.label !== ""),
    "wastewaterTreatedChartData",
  );

  const sourceArray = [
    { source: "Surface Water", color: "#FF4444" },
    { source: "Ground Water", color: "#0066E7" },
    { source: "Seawater", color: "#FFBB33" },
    { source: "Industrial Produced Water", color: "#9D2DED" },
    { source: "Third-party water", color: "#A6CDC6" },
    { source: "STP", color: "#17D451" },
    { source: "Municipal Treatment Plant - ETP", color: "#800000" },
    { source: "Others", color: "#6C679A" },
  ];

  const treatmentLevelArray = [
    { label: "Primary", color: "#FF4444" },
    { label: "Secondary", color: "#0066E7" },
    { label: "Tertiary", color: "#FFBB33" },
  ];

  const recycleArray = [
    { source: "Agriculture", color: "#FF4444" },
    { source: "Sanitation", color: "#0066E7" },
    { source: "Drinking water", color: "#FFBB33" },
    { source: "Industries", color: "#9D2DED" },
    { source: "Others", color: "#A6CDC6" },
  ];
  const waterBoxHeight = Math.max(
    280,
    (filteredWithdrawalData.length || 1) * 30 + 300,
    (filteredDischargeData.length || 1) * 30 + 300,
  );
  const waterWithdrawalHeight = Math.max(
    200,
    (filteredWithdrawalData.length || 1) * 30 + 280,
    (filteredDischargeData.length || 1) * 30 + 280,
  );
  const waterTreatedHeight = Math.max(
    200,
    (filteredWastewaterTreatedData.length || 1) * 30 + 280,
    (filteredWaterRecycledData.length || 1) * 30 + 280,
  );
  const waterTreatedBoxHeight = Math.max(
    280,
    (filteredWastewaterTreatedData.length || 1) * 30 + 300,
    (filteredWaterRecycledData.length || 1) * 30 + 300,
  );
  const TotalFuelUsedApi = async (fuelUse) => {
    try {
      // Format dates for API call
      const fromDateStr = `${fromDate.month}-${fromDate.year}`;
      const toDateStr = `${toDate.month}-${toDate.year}`;

      const response = await get(
        `/dashboard/fuelEnergyQuantityProfile/totalFuelUsed?companyId=${user?.company?._id}&based=${fuelUse}&from=${fromDateStr}&to=${toDateStr}`,
      );
      console.log(response?.data, "checking the response of the api");

      // Extract data excluding 'message' field
      const { message, ...fuelDataWithoutMessage } = response?.data;

      // Log the extracted data
      console.log("Extracted fuel data:", fuelDataWithoutMessage);

      // The data structure is an array of objects with totalValue and unit
      // We need to extract the values and create a proper structure
      if (fuelUse === "Mass Based") {
        // For Volume based: extract coal, naturalGas values
        const coalData = fuelDataWithoutMessage.coal || [];
        const naturalGasData = fuelDataWithoutMessage?.naturalGas || [];

        // Extract totalValue from each fuel type
        const coalValue = coalData[0]?.totalValue || 0;
        const naturalGasValue = naturalGasData[0]?.totalValue || 0;
        const unit = coalData[0]?.unit || naturalGasData[0]?.unit || "";

        if (unit !== "" && naturalGasValue !== 0 && coalValue !== 0) {
          setFuelData([
            {
              unit: unit,
              Coal: coalValue,
              "Natural Gas": naturalGasValue,
            },
          ]);
        } else {
          setFuelData([]);
        }

        console.log("Volume based fuel data:", {
          unit: unit,
          coal: coalValue,
          naturalGas: naturalGasValue,
        });
      } else {
        // For Mass Based: extract petrol, diesel, fuelOil values
        const petrolData = fuelDataWithoutMessage.petrol || [];
        const dieselData = fuelDataWithoutMessage.diesel || [];
        const fuelOilData = fuelDataWithoutMessage?.fuelOil || [];
        const lpgData = fuelDataWithoutMessage?.lpg || [];
        const propaneData = fuelDataWithoutMessage?.propane || [];
        // Extract totalValue from each fuel type
        const petrolValue = petrolData[0]?.totalValue || 0;
        const dieselValue = dieselData[0]?.totalValue || 0;
        const fuelOilValue = fuelOilData[0]?.totalValue || 0;
        const lpgValue = lpgData[0]?.totalValue || 0;
        const propaneValue = propaneData[0]?.totalValue || 0;
        const unit =
          petrolData[0]?.unit ||
          dieselData[0]?.unit ||
          fuelOilData[0]?.unit ||
          lpgData[0]?.unit ||
          propaneData[0]?.unit ||
          "";

        if (
          unit !== "" &&
          petrolValue !== 0 &&
          dieselValue !== 0 &&
          fuelOilValue !== 0 &&
          lpgValue !== 0 &&
          propaneValue !== 0
        ) {
          setFuelData([
            {
              unit: unit,
              Petrol: petrolValue,
              Diesel: dieselValue,
              "Fuel Oil": fuelOilValue,
              LPG: lpgValue,
              Propane: propaneValue,
            },
          ]);
        } else {
          setFuelData([]);
        }

        console.log("Mass Based fuel data:", {
          unit: unit,
          petrol: petrolValue,
          diesel: dieselValue,
          fuelOil: fuelOilValue,
          lpg: lpgValue,
          propane: propaneValue,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };
  const EnergyConsumptionApi = async (energy) => {
    try {
      // Format dates for API call
      const fromDateStr = `${fromDate.month}-${fromDate.year}`;
      const toDateStr = `${toDate.month}-${toDate.year}`;

      const response = await get(
        `/dashboard/fuelEnergyQuantityProfile/energyConsumption?companyId=${user?.company?._id}&energy=${energy}&from=${fromDateStr}&to=${toDateStr}`,
      );
      console.log(
        response?.data,
        "checking the response of the api for energy",
      );

      // Extract data excluding 'message' field
      const { message, ...energyDataWithoutMessage } = response?.data;

      // Log the extracted data
      console.log(
        "Extracted energy data for energy:",
        energyDataWithoutMessage,
      );
      console.log(
        "Available keys for energy:",
        Object.keys(energyDataWithoutMessage),
      );

      // Create energy data object with arrays
      const energyData = {
        unit: "",
        processName: [],
        machineName: [],
      };

      // Process each energy type dynamically
      Object.keys(energyDataWithoutMessage).forEach((key) => {
        const energyTypeData = energyDataWithoutMessage[key];
        if (Array.isArray(energyTypeData) && energyTypeData.length > 0) {
          const value = energyTypeData[0]?.totalValue || 0;
          const unit = energyTypeData[0]?.unit || "";

          // Set unit (use the first non-empty unit found)
          if (unit && !energyData.unit) {
            energyData.unit = unit;
          }

          // Store data in appropriate array based on energy type
          if (energy === "process") {
            energyData.processName.push(
              ...energyTypeData.map((item) => ({
                name: item?.processName || "Unknown",
                value: item?.totalUnitConsumption || 0,
              })),
            );
          } else if (energy === "machine") {
            energyData.machineName.push(
              ...energyTypeData.map((item) => ({
                name: item?.machineryName || "Unknown",
                value: item?.totalValue || 0,
              })),
            );
          }
        }
      });

      // Set the energy data state
      setEnergyData([energyData]);

      console.log("Processed energy data:", energyData);
      console.log("Process names:", energyData.processName);
      console.log("Machine names:", energyData.machineName);
    } catch (error) {
      console.error("Error:", error);
    }
  };
  const FuelUsedByProcessApi = async (fuelUseByProcess) => {
    try {
      // Format dates for API call
      const fromDateStr = `${fromDate.month}-${fromDate.year}`;
      const toDateStr = `${toDate.month}-${toDate.year}`;

      const response = await get(
        `/dashboard/fuelEnergyQuantityProfile/fuelUseProcess?companyId=${user?.company?._id}&fuelEnergy=${fuelUseByProcess}&from=${fromDateStr}&to=${toDateStr}`,
      );
      console.log(
        response?.data,
        "checking the response of the api for fuel use by process",
      );

      // Extract data excluding 'message' field
      const { message, ...fuelProcessDataWithoutMessage } = response?.data;

      // Log the extracted data
      console.log(
        "Extracted fuel process data:",
        fuelProcessDataWithoutMessage,
      );
      console.log(
        "Available keys for fuel process:",
        Object.keys(fuelProcessDataWithoutMessage),
      );

      // Create fuel process data object with arrays
      const fuelProcessData = {
        unit: "",
        processName: [],
      };

      // Process each fuel type dynamically
      Object.keys(fuelProcessDataWithoutMessage).forEach((key) => {
        const fuelTypeData = fuelProcessDataWithoutMessage[key];
        if (Array.isArray(fuelTypeData) && fuelTypeData.length > 0) {
          const unit = fuelTypeData[0]?.unit || "";

          // Set unit (use the first non-empty unit found)
          if (unit && !fuelProcessData.unit) {
            fuelProcessData.unit = unit;
          }

          // Store data in processName array based on fuelUseByProcess
          if (fuelUseByProcess === "Mass Based") {
            // For Mass Based: extract petrol, diesel, fuelOil process data
            fuelProcessData.processName.push(
              ...fuelTypeData.map((item) => ({
                name: item?.processName || key,
                value: item?.totalValue || 0,
              })),
            );
          } else if (fuelUseByProcess === "Volume based") {
            // For Volume based: extract coal, naturalGas process data
            fuelProcessData.processName.push(
              ...fuelTypeData.map((item) => ({
                name: item?.processName || key,
                value: item?.totalValue || 0,
              })),
            );
          }
        }
      });

      // Set the fuel process data state
      setFuelProcessData([fuelProcessData]);

      console.log("Processed fuel process data:", fuelProcessData);
      console.log("Process names for fuel use:", fuelProcessData.processName);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const FacilityEnergyIntensityApi = async () => {
    try {
      // Format dates for API call
      const fromDateStr = `${fromDate.month}-${fromDate.year}`;
      const toDateStr = `${toDate.month}-${toDate.year}`;

      const response = await get(
        `/dashboard/fuelEnergyQuantityProfile/facilityEnergyIntensity?companyId=${user?.company?._id}&from=${fromDateStr}&to=${toDateStr}`,
      );
      console.log(
        response?.data,
        "checking the response of the api for facility energy intensity",
      );

      // Extract data excluding 'message' field
      const { message, ...facilityEnergyDataWithoutMessage } = response?.data;

      // Log the extracted data
      console.log(
        "Extracted facility energy data:",
        facilityEnergyDataWithoutMessage,
      );
      console.log(
        "Available keys for facility energy:",
        Object.keys(facilityEnergyDataWithoutMessage),
      );

      // Create facility energy data object
      const facilityEnergyData = {
        unit: "",
        facilities: [],
        electricity: [],
        steam: [],
        heat: [],
      };

      // Process each facility dynamically
      Object.keys(facilityEnergyDataWithoutMessage).forEach((key) => {
        const facilityData = facilityEnergyDataWithoutMessage[key];
        if (Array.isArray(facilityData) && facilityData.length > 0) {
          const unit = facilityData[0]?.unit || "";

          // Set unit (use the first non-empty unit found)
          if (unit && !facilityEnergyData.unit) {
            facilityEnergyData.unit = unit;
          }

          // Extract facility name and energy consumption data
          facilityData.forEach((item, index) => {
            const facilityName = item?.facilityName || `Facility${index + 1}`;
            const electricityValue = item?.electricity || 0;
            const steamValue = item?.steam || 0;
            const heatValue = item?.heat || 0;

            // Add facility name if not already added
            if (!facilityEnergyData.facilities.includes(facilityName)) {
              facilityEnergyData.facilities.push(facilityName);
              facilityEnergyData.electricity.push(electricityValue);
              facilityEnergyData.steam.push(steamValue);
              facilityEnergyData.heat.push(heatValue);
            }
          });
        }
      });

      // Set the facility energy data state
      setFacilityEnergyData([facilityEnergyData]);

      console.log("Processed facility energy data:", facilityEnergyData);
      console.log("Facilities:", facilityEnergyData.facilities);
      console.log("Electricity data:", facilityEnergyData.electricity);
      console.log("Steam data:", facilityEnergyData.steam);
      console.log("Heat data:", facilityEnergyData.heat);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const TopFuelConsumptionProcessApi = async () => {
    console.log(fromDate, toDate, yearList, "checking for the data");
    try {
      // Format dates for API call
      const fromDateStr = `${fromDate.month}-${fromDate.year}`;
      const toDateStr = `${toDate.month}-${toDate.year}`;

      const response = await get(
        `/dashboard/fuelEnergyQuantityProfile/topFuelConsumptionProcess?companyId=${user?.company?._id}&from=${fromDateStr}&to=${toDateStr}`,
      );
      console.log(
        response?.data,
        "checking the response of the api for top fuel consumption process",
      );

      // Extract data excluding 'message' field
      const { message, ...topFuelDataWithoutMessage } = response?.data;

      // Log the extracted data
      console.log(
        "Extracted top fuel consumption data:",
        topFuelDataWithoutMessage,
      );
      console.log(
        "Available keys for top fuel consumption:",
        Object.keys(topFuelDataWithoutMessage),
      );

      // Create top fuel consumption data object
      const topFuelConsumptionData = {
        title: "Top Fuel Consuming Processes",
        items: [],
      };

      // Process each fuel consumption process dynamically
      Object.keys(topFuelDataWithoutMessage).forEach((key) => {
        const processData = topFuelDataWithoutMessage[key];
        if (Array.isArray(processData) && processData.length > 0) {
          // Extract process information
          processData.forEach((item, index) => {
            const processName =
              item?.processName || item?.name || `Process ${index + 1}`;
            const consumptionValue = item?.totalValue || item?.consumption || 0;
            const unit = item?.unit || "litres/month";

            // Format the value with unit
            const formattedValue = `${consumptionValue.toLocaleString()} ${unit}`;

            // Add to items array
            topFuelConsumptionData.items.push({
              name: processName,
              value: formattedValue,
            });
          });
        }
      });

      // Sort by consumption value (descending) and take top 3
      topFuelConsumptionData.items.sort((a, b) => {
        const aValue = parseFloat(a.value.replace(/[^\d.]/g, ""));
        const bValue = parseFloat(b.value.replace(/[^\d.]/g, ""));
        return bValue - aValue;
      });

      // Take only top 3 processes
      topFuelConsumptionData.items = topFuelConsumptionData.items.slice(0, 3);

      // Set the top fuel consumption data state
      setTopFuelConsumptionData([topFuelConsumptionData]);

      console.log(
        "Processed top fuel consumption data:",
        topFuelConsumptionData,
      );
      console.log("Top processes:", topFuelConsumptionData.items);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const TopMachinesApi = async () => {
    try {
      // Format dates for API call
      const fromDateStr = `${fromDate.month}-${fromDate.year}`;
      const toDateStr = `${toDate.month}-${toDate.year}`;

      const response = await get(
        `/dashboard/fuelEnergyQuantityProfile/topMachinesUsage?companyId=${user?.company?._id}&from=${fromDateStr}&to=${toDateStr}`,
      );
      console.log(
        response?.data,
        "checking the response of the api for top machines",
      );

      // Extract data
      const { message, topUsage, topEmissionPerUnit } = response?.data;

      // Log the extracted data
      console.log("Top Usage data:", topUsage);
      console.log("Top Emission Per Unit data:", topEmissionPerUnit);

      // Process topUsage data
      const topUsageData = {
        title: "Top Machines by Usage",
        items: [],
      };

      if (topUsage && Array.isArray(topUsage)) {
        topUsage.forEach((machine, index) => {
          const usageValue = `${machine.usage?.toLocaleString() || 0} ${
            machine.unit || ""
          }`;
          topUsageData.items.push({
            name: machine.name || `Machine ${index + 1}`,
            value: usageValue,
          });
        });
      }

      // Process topEmissionPerUnit data
      const topEmissionData = {
        title: "Top Machines by Emission per Unit",
        items: [],
      };

      if (topEmissionPerUnit && Array.isArray(topEmissionPerUnit)) {
        topEmissionPerUnit.forEach((machine, index) => {
          const emissionValue = `${
            machine.emissionPerUnit?.toFixed(2) || 0
          } CO2/${machine.unit || ""}`;
          topEmissionData.items.push({
            name: machine.name || `Machine ${index + 1}`,
            value: emissionValue,
          });
        });
      }

      // Set the top machines data state
      setTopMachinesData([topUsageData, topEmissionData]);

      console.log("Processed top machines data:", {
        topUsageData,
        topEmissionData,
      });
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const TrendFuelTypeApi = async (type) => {
    try {
      // Format dates for API call
      const fromDateStr = `${fromDate.month}-${fromDate.year}`;
      const toDateStr = `${toDate.month}-${toDate.year}`;

      const response = await get(
        `/dashboard/fuelEnergyQuantityProfile/trendFuelType?companyId=${user?.company?._id}&type=${type}`,
      );
      console.log(
        response?.data,
        "checking the response of the api for trend fuel type",
      );

      // Extract data
      const { message, ...trendDataWithoutMessage } = response?.data;

      // Log the extracted data
      console.log("Extracted trend fuel data:", trendDataWithoutMessage);
      console.log(
        "Available keys for trend fuel:",
        Object.keys(trendDataWithoutMessage),
      );

      // Process trend data for LineChart
      const trendFuelData = {
        labels: [],
        datasets: [],
        units: {},
      };

      // Define colors for different fuel types
      const colors = [
        "#FF4F4F",
        "#0E62E0",
        "#FFBB33",
        "#28a745",
        "#ffc107",
        "#17a2b8",
      ];

      // Process fuel types (diesel, naturalGas, fuelOil, etc.)
      const fuelTypes = Object.keys(trendDataWithoutMessage).filter(
        (key) =>
          key !== "message" &&
          key !== "year" &&
          Array.isArray(trendDataWithoutMessage[key]),
      );

      // Extract labels based on type
      if (type === "monthly") {
        // For monthly data, extract month numbers and create labels
        const firstFuelType = fuelTypes[0];
        if (firstFuelType && trendDataWithoutMessage[firstFuelType]) {
          trendFuelData.labels = trendDataWithoutMessage[firstFuelType].map(
            (item) => {
              const monthNames = [
                "Jan",
                "Feb",
                "Mar",
                "Apr",
                "May",
                "Jun",
                "Jul",
                "Aug",
                "Sep",
                "Oct",
                "Nov",
                "Dec",
              ];
              return monthNames[item.month - 1];
            },
          );
        }
      } else if (type === "quarter") {
        // For quarterly data, extract quarter labels
        const firstFuelType = fuelTypes[0];
        if (firstFuelType && trendDataWithoutMessage[firstFuelType]) {
          trendFuelData.labels = trendDataWithoutMessage[firstFuelType].map(
            (item) => {
              return `Q${item.quarter}`;
            },
          );
        }
      }

      // Process each fuel type
      fuelTypes.forEach((fuelType, index) => {
        const fuelData = trendDataWithoutMessage[fuelType];
        if (Array.isArray(fuelData)) {
          // Extract consumption values
          const consumptionData = fuelData.map(
            (item) => item.totalConsumption || 0,
          );

          // Extract unit from first item
          const unit = fuelData[0]?.unit || "";

          // Capitalize first letter of fuel type for display
          const displayName =
            fuelType.charAt(0).toUpperCase() + fuelType.slice(1);

          // Store unit information
          trendFuelData.units[fuelType] = unit;

          trendFuelData.datasets.push({
            label: displayName,
            data: consumptionData,
            borderColor: colors[index % colors.length],
            backgroundColor: colors[index % colors.length],
            borderWidth: 2,
            fill: false,
            tension: 0.1,
          });
        }
      });

      // Set the trend fuel data state
      setTrendFuelData(trendFuelData);

      console.log("Processed trend fuel data:", trendFuelData);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const ProcessWiseEnergyConsumptionApi = async () => {
    try {
      const fromDateStr = `${fromDate.month.toString().padStart(2, "0")}-${
        fromDate.year
      }`;
      const toDateStr = `${toDate.month.toString().padStart(2, "0")}-${
        toDate.year
      }`;

      const response = await get(
        `/dashboard/fuelEnergyQuantityProfile/ProcessWiseEnergyConsumption?companyId=${user?.company?._id}&from=${fromDateStr}&to=${toDateStr}`,
      );

      console.log("Process-wise Energy Consumption API Response:", response);

      if (response?.data?.data && Array.isArray(response.data.data)) {
        const processData = response.data.data;

        // Log the extracted data
        console.log("Extracted process energy data:", processData);

        // Set the process energy data state
        setProcessEnergyData(processData);

        console.log("Processed process energy data:", processData);
      }
    } catch (error) {
      console.error(
        "Error fetching process-wise energy consumption data:",
        error,
      );
    }
  };

  const TotalFuelUsed = (e) => {
    console.log(e.target.value, "checking the value of the dropdown");
    setFuelUse(e.target.value);
    TotalFuelUsedApi(e.target.value);
  };
  const FuelUsedByProcess = (e) => {
    setFuelUseByProcess(e.target.value);
    FuelUsedByProcessApi(e.target.value);
  };
  const EnergyConsumption = (e) => {
    setEnergyIntensityType(e.target.value);
    EnergyConsumptionApi(e.target.value);
  };

  const handleTemporalTypeChange = (e) => {
    const newType = e.target.value;
    console.log(
      newType,
      "checking the value of the dropdown for temporal type",
    );
    setTemporalType(newType);

    // Reset temporal period to first option of new type
    const newOptions = getTemporalPeriodOptions(newType);
    if (newOptions.length > 0) {
      setTemporalPeriod(newOptions[0]);
    }

    // Call API with new temporal type
    if (activeView === "energy") {
      TrendFuelTypeApi(newType);
    }
  };

  const handleTemporalPeriodChange = (e) => {
    setTemporalPeriod(e.target.value);
    // Call API with new temporal period
    if (activeView === "energy") {
      TrendFuelTypeApi(emissionType);
    }
  };

  console.log("data11", fuelData);

  return (
    <>
      {showCompany ? (
        <div className={styles.dashboard_header}>
          <p className={styles.heading}>Environment Dashboard</p>
          <div className={styles.dashboard_header_right}>
            <img
              src={user?.company?.companyImage}
              alt={"company"}
              className={styles.company_image}
            />
            <div className={styles.company_name_wrapper}>
              {user?.company?.companyName.split(" ").map((word, idx) => (
                <React.Fragment key={idx}>
                  {word}
                  {(idx + 1) % 2 === 0 ? <br /> : " "}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <p className={styles.heading}>Environment Dashboard</p>
      )}

      <div className={styles.dashboard_container}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "20px",
            marginBottom: "20px",
            width: "100%",
          }}
        >
          <button
            onClick={() => handleViewSwitch("emissions")}
            className={`${styles.view_button} ${
              activeView === "emissions" ? styles.view_button_active : ""
            }`}
          >
            Emissions and Water
          </button>
          <button
            onClick={() => handleViewSwitch("energy")}
            className={`${styles.view_button} ${
              activeView === "energy" ? styles.view_button_active : ""
            }`}
          >
            Energy and Fuel
          </button>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            marginBottom: "20px",
          }}
        >
          <p className={styles.sub_heading}>
            {activeView === "emissions"
              ? "GHG Accounting"
              : "I.Fuel & Energy Quantity Profile (Consumption Hotspots)"}
          </p>

          <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
            <PeriodFilter
              {...{
                fromDate,
                setFromDate,
                toDate,
                setToDate,
                yearList,
                setSelectedFacility,
                selectedFacility,
                facilityRequired: "true",
              }}
            />
          </div>
        </div>
      </div>

      {activeView === "emissions" ? (
        <div className={styles.graphsSection}>
          <div className={styles.graphCard}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <p className={styles.data_card_title}>
                Total GHG Emissions (tCO2e):{" "}
                <span className={styles.graph_span}>
                  {emissionType === "Annual"
                    ? formatNumberCommaSeparated(
                        (
                          Number(overviewData?.scope1?.totalEmissionSum ?? 0) +
                          Number(
                            overviewData?.scope2?.totalFinalEmission ?? 0,
                          ) +
                          Number(overviewData?.scope3?.totalSum ?? 0)
                        ).toFixed(3),
                      )
                    : formatNumberCommaSeparated(
                        (
                          Number(overviewData?.scope1?.last10yearsum ?? 0) +
                          Number(overviewData?.scope2?.last10yearsum ?? 0) +
                          Number(overviewData?.scope3?.last10yearsum ?? 0)
                        ).toFixed(3),
                      )}
                </span>
              </p>
              <div>
                <select
                  name="month"
                  id="month"
                  value={emissionType}
                  onChange={(e) => setEmissionType(e.target.value)}
                  className={styles.heading_select_month}
                >
                  {["Annual", "Year On Year"].map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <LineChart
              dataset={
                activeView === "energy"
                  ? TrendFuelData.datasets
                  : emissionType === "Annual"
                    ? dataset
                    : yoydataset
              }
              labels={
                activeView === "energy"
                  ? TrendFuelData.labels
                  : emissionType === "Annual"
                    ? overviewData?.scope1?.scopoe1MonthlEmission?.data?.map(
                        (el) =>
                          monthSort[el?.month - 1] +
                          "'" +
                          el?.year.toString().slice(-2),
                      )
                    : overviewData?.scope1?.scope1Last10YearData?.map(
                        (d) => d.year,
                      )
              }
              yAxisTitle={
                activeView === "energy"
                  ? "Fuel Consumption"
                  : "GHG Emissions (tCO2e)"
              }
              display={true}
            />
          </div>
        </div>
      ) : (
        <div>
          <div className={styles.graphsSection}>
            <div className={styles.graphCardNoBackground}>
              <div style={{ display: "flex", gap: "20px", marginTop: "10px" }}>
                <div className={styles.energyChartCard}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "15px",
                    }}
                  >
                    <h4
                      style={{
                        margin: 0,
                        fontSize: "16px",
                        fontWeight: "600",
                        color: "rgba(0, 0, 0, 0.9)",
                      }}
                    >
                      Total Quantity of Fuel Used
                    </h4>
                    <select
                      value={fuelUse}
                      onChange={(e) => TotalFuelUsed(e)}
                      style={{
                        padding: "4px 8px",
                        border: "1px solid rgba(0, 0, 0, 0.2)",
                        borderRadius: "4px",
                        fontSize: "12px",
                      }}
                    >
                      <option value="Mass Based">Mass Based</option>
                      <option value="Volume based">Volume based</option>
                    </select>
                  </div>
                  <BarChart
                    datasets={[
                      {
                        label: "Fuel Consumption",
                        data: fuelData[0]
                          ? Object.entries(fuelData[0])
                              .filter(([key]) => key !== "unit")
                              .map(([, value]) => value)
                          : [],
                        backgroundColor: "#0051B7",
                        borderColor: "#0051B7",
                        borderWidth: 1,
                        barPercentage: 0.3,
                        categoryPercentage: 0.9,
                      },
                    ]}
                    labels={
                      fuelData[0]
                        ? Object.keys(fuelData[0]).filter(
                            (key) => key !== "unit",
                          )
                        : []
                    }
                    customWidth={"100%"}
                    customHeight={200}
                    yAxisTitle={`Quantity (${fuelData[0]?.unit || ""})`}
                    display={false}
                    options={{
                      scales: {
                        x: {
                          ticks: {
                            padding: 10,
                            font: {
                              size: 12,
                            },
                          },
                          grid: {
                            display: false,
                          },
                        },
                        y: {
                          type: "logarithmic",
                          beginAtZero: false,
                          ticks: {
                            padding: 8,
                            font: {
                              size: 11,
                            },
                          },
                        },
                      },
                      plugins: {
                        legend: {
                          display: false,
                        },
                      },
                      layout: {
                        padding: {
                          top: 20,
                          bottom: 20,
                        },
                      },
                    }}
                  />
                </div>
                <div className={styles.energyChartCard}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "15px",
                    }}
                  >
                    <h4
                      style={{
                        margin: 0,
                        fontSize: "16px",
                        fontWeight: "600",
                        color: "rgba(0, 0, 0, 0.9)",
                      }}
                    >
                      Energy Consumption
                    </h4>
                    {console.log(
                      EnergyData,
                      "checking the value of the dropdown for energy",
                    )}
                    <select
                      value={energyIntensityType}
                      onChange={(e) => EnergyConsumption(e)}
                      style={{
                        padding: "4px 0px",
                        border: "1px solid rgba(0, 0, 0, 0.2)",
                        borderRadius: "4px",
                        fontSize: "12px",
                      }}
                    >
                      <option value="machine">Machine</option>
                      <option value="process">Process</option>
                    </select>
                  </div>
                  <BarChart
                    datasets={[
                      {
                        label: "Energy Consumption",
                        data: EnergyData[0]
                          ? energyIntensityType === "process"
                            ? EnergyData[0].processName.map(
                                (item) => item.value,
                              )
                            : EnergyData[0].machineName.map(
                                (item) => item.value,
                              )
                          : [],
                        backgroundColor: "#FFBB33",
                        borderColor: "#FFBB33",
                        borderWidth: 1,
                        barPercentage: 0.3,
                        categoryPercentage: 0.9,
                      },
                    ]}
                    labels={
                      EnergyData[0]
                        ? energyIntensityType === "process"
                          ? EnergyData[0].processName.map((item) => item.name)
                          : EnergyData[0].machineName.map((item) => item.name)
                        : []
                    }
                    customWidth={"100%"}
                    customHeight={200}
                    yAxisTitle={`Quantity (${EnergyData[0]?.unit || "KWH"})`}
                    display={false}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className={styles.graphCardNoBackground}>
            <div style={{ display: "flex", gap: "20px", marginTop: "10px" }}>
              <div className={styles.energyChartCard}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "15px",
                  }}
                >
                  <h4
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "rgba(0, 0, 0, 0.9)",
                    }}
                  >
                    Fuel Use by Process
                  </h4>
                  <select
                    value={fuelUseByProcess}
                    onChange={(e) => FuelUsedByProcess(e)}
                    style={{
                      padding: "4px 8px",
                      border: "1px solid rgba(0, 0, 0, 0.2)",
                      borderRadius: "4px",
                      fontSize: "12px",
                    }}
                  >
                    <option value="Mass Based">Mass Based</option>
                    <option value="Volume based">Volume based</option>
                  </select>
                </div>
                <BarChart
                  datasets={[
                    {
                      label: "Fuel Consumption",
                      data: FuelProcessData[0]
                        ? FuelProcessData[0].processName.map(
                            (item) => item.value,
                          )
                        : [],
                      backgroundColor: "#0051B7",
                      borderColor: "#0051B7",
                      borderWidth: 1,
                      barPercentage: 0.3,
                      categoryPercentage: 0.9,
                    },
                  ]}
                  labels={
                    FuelProcessData[0]
                      ? FuelProcessData[0].processName.map((item) => item.name)
                      : []
                  }
                  customWidth={"100%"}
                  customHeight={200}
                  yAxisTitle={`Quantity (${FuelProcessData[0]?.unit || ""})`}
                  display={false}
                />
              </div>
              <div className={styles.energyChartCard}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "15px",
                  }}
                >
                  <h4
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "rgba(0, 0, 0, 0.9)",
                    }}
                  >
                    Process-wise Energy Intensity
                  </h4>
                </div>
                <BarChart
                  datasets={[
                    {
                      label: "Process Energy Consumption",
                      data:
                        ProcessEnergyData.length > 0
                          ? ProcessEnergyData.map(
                              (item) => item.totalUnitConsumption,
                            )
                          : [],
                      backgroundColor: "#0051B7",
                      borderColor: "#0051B7",
                      borderWidth: 1,
                      barPercentage: 0.3,
                      categoryPercentage: 0.9,
                    },
                  ]}
                  labels={
                    ProcessEnergyData.length > 0
                      ? ProcessEnergyData.map((item) => item.processName)
                      : []
                  }
                  customWidth={"100%"}
                  customHeight={200}
                  yAxisTitle={
                    ProcessEnergyData.length > 0
                      ? `Quantity (${
                          ProcessEnergyData[0]?.totalUnitConsumptionUnit || ""
                        })`
                      : "Quantity"
                  }
                  display={false}
                />
              </div>
            </div>
          </div>
          <div className={styles.facilityEnergyCard}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "15px",
              }}
            >
              <h4
                style={{
                  margin: 0,
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "rgba(0, 0, 0, 0.9)",
                }}
              >
                Facility-wise Energy Consumption
              </h4>
            </div>
            <BarChart
              datasets={[
                {
                  label: "Electricity",
                  data: FacilityEnergyData[0]
                    ? FacilityEnergyData[0].electricity
                    : [],
                  backgroundColor: "#FF4F4F",
                  borderColor: "#FF4F4F",
                  borderWidth: 1,
                  barPercentage: 0.3,
                  categoryPercentage: 0.9,
                },
                {
                  label: "Steam",
                  data: FacilityEnergyData[0]
                    ? FacilityEnergyData[0].steam
                    : [],
                  backgroundColor: "#0E62E0",
                  borderColor: "#0E62E0",
                  borderWidth: 1,
                  barPercentage: 0.3,
                  categoryPercentage: 0.9,
                },
                {
                  label: "Heat",
                  data: FacilityEnergyData[0] ? FacilityEnergyData[0].heat : [],
                  backgroundColor: "#FFBB33",
                  borderColor: "#FFBB33",
                  borderWidth: 1,
                  barPercentage: 0.3,
                  categoryPercentage: 0.9,
                },
              ]}
              labels={
                FacilityEnergyData[0] ? FacilityEnergyData[0].facilities : []
              }
              customWidth={"100%"}
              customHeight={300}
              yAxisTitle={`Energy Consumption (${
                FacilityEnergyData[0]?.unit || ""
              })`}
              display={true}
            />
          </div>

          {/* II. High-Impact Contributor Identification (Hotspot Analysis) */}
          <div style={{ marginTop: "2rem" }}>
            <h3
              style={{
                fontSize: "20px",
                fontWeight: "700",
                color: "black",
                marginBottom: "1.5rem",
                margin: "0 0 1.5rem 0",
              }}
            >
              II. High-Impact Contributor Identification (Hotspot Analysis)
            </h3>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: "1.5rem",
              }}
            >
              {[...TopFuelConsumptionData, ...TopMachinesData].map(
                (card, index) => (
                  <div key={index} className={styles.hotspotCard}>
                    <h4
                      style={{
                        margin: "0 0 1rem 0",
                        fontSize: "16px",
                        fontWeight: "600",
                        color: "rgba(0, 0, 0, 0.9)",
                      }}
                    >
                      {card.title}
                    </h4>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.5rem",
                      }}
                    >
                      {card.items.length > 0 ? (
                        card.items.map((item, itemIndex) => (
                          <div
                            key={itemIndex}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              padding: "0.5rem 0",
                              borderBottom:
                                itemIndex < card.items.length - 1
                                  ? "1px solid rgba(0, 0, 0, 0.1)"
                                  : "none",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "14px",
                                fontWeight: "500",
                                color: "rgba(0, 0, 0, 0.8)",
                              }}
                            >
                              {item.name}
                            </span>
                            <span
                              style={{
                                fontSize: "14px",
                                fontWeight: "600",
                                color: "rgba(0, 0, 0, 0.9)",
                              }}
                            >
                              {item.value}
                            </span>
                          </div>
                        ))
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            padding: "2rem 0",
                            color: "rgba(0, 0, 0, 0.5)",
                            fontSize: "14px",
                            fontWeight: "500",
                          }}
                        >
                          NO Data Found
                        </div>
                      )}
                    </div>
                  </div>
                ),
              )}
            </div>
          </div>

          {/* II. Temporal Trends & Deviation Insights */}
          <div style={{ marginTop: "2rem" }}>
            <h3
              style={{
                fontSize: "20px",
                fontWeight: "700",
                color: "black",
                marginBottom: "1.5rem",
                margin: "0 0 1.5rem 0",
              }}
            >
              II. Temporal Trends & Deviation Insights
            </h3>

            <div className={styles.temporalTrendsCard}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "1.5rem",
                }}
              >
                <h4
                  style={{
                    margin: 0,
                    fontSize: "18px",
                    fontWeight: "600",
                    color: "rgba(0, 0, 0, 0.9)",
                  }}
                >
                  Monthly/Quarterly Trend of Fuel Types
                </h4>

                <div
                  style={{ display: "flex", gap: "1rem", alignItems: "center" }}
                >
                  <select
                    value={temporalType}
                    onChange={handleTemporalTypeChange}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid rgba(0, 0, 0, 0.2)",
                      borderRadius: "4px",
                      fontSize: "14px",
                      minWidth: "120px",
                    }}
                  >
                    <option value="monthly">Monthly</option>
                    <option value="quarter">Quarterly</option>
                  </select>

                  {/* <select
                    value={temporalPeriod}
                    onChange={handleTemporalPeriodChange}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid rgba(0, 0, 0, 0.2)",
                      borderRadius: "4px",
                      fontSize: "14px",
                      minWidth: "120px",
                    }}
                  >
                    {getTemporalPeriodOptions(temporalType).map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select> */}
                </div>
              </div>

              <LineChart
                dataset={temporalTrendsData}
                labels={temporalTrendsLabels}
                yAxisTitle={"Energy Unit Litres"}
                display={true}
              />
            </div>

            {/* KPI Card Below Line Chart */}
            <div className={styles.kpiCard}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <span
                    style={{
                      fontSize: "18px",
                      fontWeight: "600",
                      color: "rgba(0, 0, 0, 0.9)",
                    }}
                  >
                    2000 meter cube
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    marginTop: "0.5rem",
                  }}
                >
                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#22C55E",
                    }}
                  >
                    +1% YOY
                  </span>
                  <TrendingUp
                    style={{
                      color: "#22C55E",
                      fontSize: "20px",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeView === "emissions" && (
        <>
          <div className={styles.ghg_container}>
            <div className={styles.ghg_container_one}>
              <div className={styles.ghg_container_one_card}>
                <DataCard
                  title={"Total Scope 1 emissions (tCO2e): "}
                  titleValue={`${formatNumberCommaSeparated(
                    totalData?.scope1?.totalScope1Emission
                      ? parseFloat(
                          totalData.scope1.totalScope1Emission,
                        ).toFixed(3)
                      : "0",
                  )}`}
                  data={scope1Data}
                />
                <DataCard
                  title={"Total Scope 2 emissions (tCO2e): "}
                  titleValue={`${formatNumberCommaSeparated(
                    totalData?.scope2?.totalScope2Emission
                      ? parseFloat(
                          totalData.scope2.totalScope2Emission,
                        ).toFixed(3)
                      : "0",
                  )}`}
                  data={scope2Data}
                />
              </div>
              <div className={styles.ghg_container_one_text}>
                <p>
                  GHG Emissions Intensity:{" "}
                  <span className={styles.span_ghg}>
                    {truncateNumber(
                      (Number(overviewData?.scope1?.totalEmissionSum ?? 0) +
                        Number(overviewData?.scope2?.totalFinalEmission ?? 0) +
                        Number(overviewData?.scope3?.totalSum ?? 0)) /
                        quantityProduced,
                    )}
                  </span>
                </p>
              </div>
            </div>
            <div className={styles.ghg_container_two}>
              <DataCard
                title={"Total Scope 3 emissions (tCO2e): "}
                titleValue={formatNumberCommaSeparated(
                  totalData?.scope3?.totalScope3Emission
                    ? parseFloat(
                        totalData?.scope3?.totalScope3Emission,
                      ).toFixed(3)
                    : "0",
                )}
                data={scope3Data}
              />
            </div>
          </div>

          <p className={styles.sub_heading}>Water</p>

          <div
            className={styles.graph_container}
            style={{
              height: `${waterBoxHeight}px`,
            }}
          >
            <div
              className={styles.doughnut_container}
              style={{
                height: `${waterWithdrawalHeight}px`,
              }}
            >
              <p className={styles.cards_heading}>
                Water Withdrawal (KL):{" "}
                {formatNumberCommaSeparated(
                  filteredWithdrawalData.reduce(
                    (acc, curr) => acc + curr.total,
                    0,
                  ) ?? 0,
                )}
              </p>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <EchartDoughnutChart
                  labels={filteredWithdrawalData.map((item) => item.label)}
                  dataPoints={filteredWithdrawalData.map((item) => item.total)}
                  colors={filteredWithdrawalData.map(
                    (el) =>
                      sourceArray?.find((e) => el?.label === e.source)?.color,
                  )}
                  customWidth="200px"
                  customHeight="200px"
                  legendPosition="top"
                  mt="40%"
                />
              </div>
              <div className={styles.doughnut_table_div}>
                {filteredWithdrawalData.map((item, index) => (
                  <div key={index} className={styles.table_container}>
                    <div className={styles.table_row}>
                      <span
                        className={styles.table_row_label}
                        style={{
                          backgroundColor: sourceArray?.find(
                            (e) => item?.label === e.source,
                          )?.color,
                        }}
                      ></span>
                      <span className={styles.table_text}>{item.label}</span>
                    </div>
                    <span className={styles.table_text}>
                      {formatNumberCommaSeparated(item.total)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div
              className={styles.doughnut_container}
              style={{
                height: `${waterWithdrawalHeight}px`,
              }}
            >
              <p className={styles.cards_heading}>
                Water Discharge (KL):{" "}
                {formatNumberCommaSeparated(
                  filteredDischargeData.reduce(
                    (acc, curr) => acc + curr.total,
                    0,
                  ) ?? 0,
                )}
              </p>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <EchartDoughnutChart
                  labels={filteredDischargeData.map((item) => item.label)}
                  dataPoints={filteredDischargeData.map((item) => item.total)}
                  colors={filteredDischargeData.map(
                    (el) =>
                      sourceArray?.find((e) => el?.label === e.source)?.color,
                  )}
                  customWidth="200px"
                  customHeight="200px"
                  legendPosition="top"
                  mt="40%"
                />
              </div>
              <div className={styles.doughnut_table_div}>
                {filteredDischargeData.map((item, index) => (
                  <div key={index} className={styles.table_container}>
                    <div className={styles.table_row}>
                      <span
                        className={styles.table_row_label}
                        style={{
                          backgroundColor: sourceArray?.find(
                            (e) => item?.label === e.source,
                          )?.color,
                        }}
                      ></span>
                      <span className={styles.table_text}>{item.label}</span>
                    </div>
                    <span className={styles.table_text}>
                      {formatNumberCommaSeparated(item.total)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div
            className={styles.graph_container}
            style={{
              height: `${waterTreatedBoxHeight}px`,
            }}
          >
            <div
              className={styles.doughnut_container}
              style={{
                height: `${waterTreatedHeight}px`,
              }}
            >
              <p className={styles.cards_heading}>
                Wastewater Treated (KL):{" "}
                {formatNumberCommaSeparated(
                  filteredWastewaterTreatedData.reduce(
                    (acc, curr) => acc + curr.total,
                    0,
                  ) ?? 0,
                )}
              </p>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <EchartDoughnutChart
                  labels={filteredWastewaterTreatedData.map(
                    (item) => item.label,
                  )}
                  dataPoints={filteredWastewaterTreatedData.map(
                    (item) => item.total,
                  )}
                  colors={filteredWastewaterTreatedData.map(
                    (el) =>
                      treatmentLevelArray?.find((e) => el?.label === e.label)
                        ?.color,
                  )}
                  customWidth="200px"
                  customHeight="200px"
                  legendPosition="top"
                  mt="40%"
                />
              </div>
              <div className={styles.doughnut_table_div}>
                {filteredWastewaterTreatedData.map((item, index) => (
                  <div key={index} className={styles.table_container}>
                    <div className={styles.table_row}>
                      <span
                        className={styles.table_row_label}
                        style={{
                          backgroundColor: treatmentLevelArray?.find(
                            (e) => item?.label === e.label,
                          )?.color,
                        }}
                      ></span>
                      <span className={styles.table_text}>{item.label}</span>
                    </div>
                    <span className={styles.table_text}>
                      {formatNumberCommaSeparated(item.total)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div
              className={styles.doughnut_container}
              style={{
                height: `${waterTreatedHeight}px`,
              }}
            >
              <p className={styles.cards_heading}>
                Water Circularity (KL):{" "}
                {formatNumberCommaSeparated(
                  filteredWaterRecycledData.reduce(
                    (acc, curr) => acc + curr.total,
                    0,
                  ) ?? 0,
                )}
              </p>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <EchartDoughnutChart
                  labels={filteredWaterRecycledData.map((item) => item.label)}
                  dataPoints={filteredWaterRecycledData.map(
                    (item) => item.total,
                  )}
                  colors={filteredWaterRecycledData.map(
                    (el) =>
                      recycleArray?.find((e) => el?.label === e.source)?.color,
                  )}
                  customWidth="200px"
                  customHeight="200px"
                  legendPosition="top"
                  mt="40%"
                />
              </div>
              <div className={styles.doughnut_table_div}>
                {filteredWaterRecycledData.map((item, index) => (
                  <div key={index} className={styles.table_container}>
                    <div className={styles.table_row}>
                      <span
                        className={styles.table_row_label}
                        style={{
                          backgroundColor: recycleArray?.find(
                            (e) => item?.label === e.source,
                          )?.color,
                        }}
                      ></span>
                      <span className={styles.table_text}>{item.label}</span>
                    </div>
                    <span className={styles.table_text}>
                      {formatNumberCommaSeparated(item.total)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Dashboard;
